import { type Context, useContext, useEffect, useRef } from 'react';
import { UserContext } from 'context-providers/user-provider';
import { CartContext } from 'context-providers/cart-provider';
import { ProductsContext } from 'context-providers/products-provider';
import { trackEvent } from 'lib/analytics';
import { getFromLocalStorage } from 'lib/utils/get-from-local-storage';
import { getFromSessionStorage } from 'lib/utils/get-from-session-storage';
import { setToSessionStorage } from 'lib/utils/set-to-session-storage';

const PENDING_FLAG_KEY = 'pending_shopify_login';

/**
 * Fires Login and Checkout Step Completed analytics events after a Shopify OAuth
 * redirect login. The Shopify sign-in flow does a full-page redirect to Shopify
 * and then back, so we can't fire events inline like regular/Google login.
 *
 * Instead, shopify-signin.tsx sets a sessionStorage flag before redirect, and this
 * hook consumes it once the user is authenticated on the return trip.
 */
const useShopifyLoginAnalytics = () => {
  const { userState } = useContext(UserContext);
  // CartContext is a conditional export (Shopify | Recurly) with slightly different types
  const { userCart } = useContext(
    CartContext as unknown as Context<{ userCart: any }>,
  );
  const { products } = useContext(ProductsContext);
  const hasFired = useRef(false);

  useEffect(() => {
    if (hasFired.current) return;

    const pending = getFromSessionStorage(PENDING_FLAG_KEY);
    if (!pending) return;

    // Wait until userState is populated (checkUserAuthorization has completed)
    if (!userState?.externalId) return;

    // Clear flag immediately to prevent duplicate fires
    setToSessionStorage(PENDING_FLAG_KEY, null);
    hasFired.current = true;

    const cartItems = getFromLocalStorage('seed_shopify_cart_items');

    trackEvent('Login', {
      info: 'Login',
      social: 'Shopify',
      source: 'navbar',
      cartItems,
    });
  }, [userState, userCart, products]);
};

export default useShopifyLoginAnalytics;
